import CustomError from './CustomError';
import Utilities from './Utilities';
// ------------------------------------------------ internals ------------------------------------------------ //
/**
 * Currently supported response mime types with type check and decoding functionality
 * @type {*[]}
 */
const SUPPORTED_RESPONSE_MIME_TYPES = [
  {
    mime: 'text/plain',
    check: data => typeof data === 'string' && !Utilities.isJSON(data),
    decode: data => data,
  },
  {
    mime: 'application/json',
    check: data => typeof data === 'string' && Utilities.isJSON(data),
    decode: data => JSON.parse(data),
  },
];
/**
 * Gets the origin from window.location.origin or builds it when not provided via location properties (protocol, hostname, ?port)
 *
 * @return {String} The current origin
 */
const getOrigin = () => window.location.origin
  || `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}`;

/**
 * Check if the given url fulfills the same origin policy
 *
 * @param {String} url - The url to check
 * @return {Boolean} Whether it fulfills the same origin policy or not
 */
const isSameOrigin = (url) => {
  const origin = getOrigin();
  return url === origin || url.slice(0, origin.length + 1) === `${origin}/`;
};

/**
 * Checks if current browser have to use XDomainRequest for supporting CORS
 *
 * @param {String} url - Is necessary to determine if request will cross the current origin
 * @return {Boolean} Whether the current browser should use XDomainRequest or not
 */
const shouldUseXDR = (url) => {
  const crossOrigin = isSameOrigin(url) === false;
  const xhrExists = typeof window.XMLHttpRequest !== 'undefined';
  const xdrExists = typeof window.XDomainRequest !== 'undefined';
  const noCORS = xhrExists && !('withCredentials' in new XMLHttpRequest());
  return crossOrigin === true && noCORS === true && xdrExists === true;
};
/**
 * Resolves the http interface usage in the current browser. The method will return by default the XMLHttpRequest interface because it's commonly used
 * by most browsers out in the wild. But we have to be compatible to old IE's and we have to use CORS when necessary. So we check first if we have to
 * use CORS for the request and if the browser should fallback on XDomainRequest because he is an old IE (>= 8 & < 10) with no CORS support in the
 * XMLHttpRequest interface. With this implementation we support CORS right down to IE 8.
 *
 * @param {String} url - A url to determine if CORS is necessary
 * @return {{Request:(XDomainRequest|XMLHttpRequest), type: string}} Returns an object with the engine implementation and a type declaration
 */
const resolveAjax = (url) => {
  let ajax;
  // Check if CORS is necessary and check if supported by current browser
  // When not fallback to XDomainRequest
  if (shouldUseXDR(url) === true) {
    // Use XDomainRequest interface for CORS
    ajax = { Request: window.XDomainRequest, type: 'xdr' };
  } else if (XMLHttpRequest) {
    // Use XMLHttpRequest (by far the most used clause)
    ajax = { Request: window.XMLHttpRequest, type: 'xhr' };
  }
  return ajax;
};

/**
 * Guesses the mime type of the response text and decode to the desired output (e.g. JSON -> Javascript)
 *
 * @param {*} responseText - The response text to convert
 * @return {{data: *, mime: String}} Result as object with data and mime property
 */
const convertResponse = (responseText) => {
  const result = { mime: undefined, data: undefined };
  if (responseText) {
    for (let i = 0, iLen = SUPPORTED_RESPONSE_MIME_TYPES.length; i < iLen; i += 1) {
      const mimeType = SUPPORTED_RESPONSE_MIME_TYPES[i];
      if (mimeType.check(responseText) === true) {
        result.mime = mimeType.mime;
        result.data = mimeType.decode(responseText);
      }
    }
  }
  return result;
};

/**
 * Method for processing the response. Both available interfaces XDomainRequest and XMLHttpRequest are considered.
 *
 * @param {Sensor} sensor - The current sensor instance
 * @param {XMLHttpRequest|XDomainRequest} request
 * @param {String} requestType - The request type xhr or xdr
 * @param {String} url - The request url
 * @param {Number} startTime - Complexity start timer
 * @param {String} timeCode - Time code for the complexity metric
 * @param {Function} resolve - The promise resolve function
 */
const processResponse = (sensor, request, requestType, url, startTime, timeCode, resolve) => {
  const {
    state,
    logger,
  } = sensor;
  // Guess mime type an decode response text
  const { data } = convertResponse(request.responseText);
  if (requestType === 'xhr' && request.status >= 400) {
    // Error handling exclusive for XMLHttpRequest interface
    const message = `Request communication error with target '${url}' (Status Code: ${request.status})`;
    logger.debug(message);
    state.scriptErrors.push(new CustomError('E13', message));
    state.runtimes += `${timeCode}:${(new Date()).getTime() - startTime}`;
    resolve(message);
  } else {
    // Default processing for XDomainRequest and XMLHttpRequest interface
    // Log response to browser console
    logger.debug(`${url} by ${requestType.toUpperCase()}`, data);
    state.runtimes += `${timeCode}:${(new Date()).getTime() - startTime}`;
    resolve(data);
  }
};
/**
 * Sends the request with body when provided.
 *
 * @param {XDomainRequest|XMLHttpRequest} request - The request object
 * @param {String} requestType - The request type (xhr or xdr)
 * @param {*} [body] - Optional body
 */
function send(request, requestType, body) {
  if (body !== undefined) {
    //  Stringify the request body to JSON by default when it's not already stringified
    const data = Utilities.isJSON(body) ? body : JSON.stringify(body);
    if (requestType === 'xhr') {
      // When we use the XMLHttpRequest we can set the content type of the request as HTTP header
      // The XDomainRequest interface does not support any header and defaults to 'Content-Type' : 'text/plain'
      request.setRequestHeader('Content-Type', 'application/json; charset=utf-8');
    }
    request.send(data);
  } else {
    request.send();
  }
}

// ------------------------------------------------ public ------------------------------------------------ //

/**
 * HTTP class for communicating via HTTP
 */
export default class Http {
  /**
   * Creates a request object from the supported ajax interface (XMLHttpRequest or XDomainRequest) and returns it
   *
   * @param {Sensor} sensor - The sensor instance (needed for error reporting to the state)
   * @param {String} url - The target url of the request
   * @param {String} [method] - The optional HTTP method (defaults to GET)
   * @param {Number} [timeout] - Optional timeout (deactivated by default)
   * @return {undefined|{request: (XMLHttpRequest|XDomainRequest), type: string}}
   */
  static createRequest(sensor, url, method = 'GET', timeout = 0) {
    const { state, logger } = sensor;
    const ajax = resolveAjax(url);
    if (ajax) {
      // eslint-disable-next-line no-param-reassign
      method = method.toUpperCase();
      // Extract request interface and type
      const { Request, type } = ajax;
      // Initialize new instance
      const request = new Request();
      // Create asynchronous request by default for XMLHttpRequest interface
      // On XDomainRequest interface the async flag is ignored.
      request.open(method, url, type === 'xhr');
      // Set timeout when supported. It's a feature which is not implemented when spec was first released so we have to check it before assigning a
      // value to it. See https://docs.w3cub.com/dom/xmlhttprequest/ for compatibility matrix
      if ('timeout' in request) {
        request.timeout = timeout;
      }
      return { request, type };
    }
    const message = 'Current browser does not support ajax.';
    logger.debug(message);
    state.scriptErrors.push(new CustomError('E5', message));
    return { request: undefined, type: undefined };
  }

  /**
   * Sends a request to the target url with a given HTTP method. It checks the url cache before attempting the target and resolves with the cached
   * response when caching is enabled and the request identified by it's url is already cached. When cached is also looks for the ttl of the cached
   * request and remove it when ttl reached.
   *
   * @param {Sensor} sensor - The current sensor instance
   * @param {String} type - The request type (specifies if the request is cacheable)
   * @param {String} url - The url for the request
   * @param [method] - Optional method, defaults to GET
   * @param [body] - Optional body, defaults to undefined. If set the method is PUT or POST
   * @param [startTime] - Optional start time for this process
   * @param [timeCode] - Optional time code for time complexity metrics
   * @return {Promise<*>} Returns a promise which always resolves
   */
  static sendRequest(sensor, type, url, method = 'GET', body = undefined, startTime = new Date().getTime(), timeCode = 'T1', timeout = 4000) {
    return new Promise((resolve) => {
      const {
        logger,
        state,
      } = sensor;
      // eslint-disable-next-line no-param-reassign
      method = method.toUpperCase();
      if (method === 'GET' && body !== undefined) {
        // Empty body for GET requests
        // eslint-disable-next-line no-param-reassign
        body = undefined;
      }
      const noop = () => {};
      const { request, type: requestType } = Http.createRequest(sensor, url, method, timeout);
      if (request !== undefined && requestType !== undefined) {
        // Create request handler for timeout, error and response
        /**
         * Timeout handler for the request
         */
        request.ontimeout = () => {
          const message = `Request timeout for target '${url}' after ${timeout} ms.`;
          logger.debug(message);
          state.scriptErrors.push(new CustomError('E12', message));
          state.runtimes += `${timeCode}:${new Date().getTime() - startTime}`;
          resolve();
        };
        /**
         * Error handler for the request
         */
        request.onerror = () => {
          const message = `Request communication error (onerror) for target '${url}'.`;
          logger.debug(message);
          state.scriptErrors.push(new CustomError('E14', message));
          state.runtimes += `${timeCode}:${(new Date()).getTime() - startTime}`;
          resolve();
        };
        if (requestType === 'xdr') {
          // XDomainRequest doesn't implement the loadend event so we bound it to the load event which is the same
          request.onload = () => processResponse(sensor, request, requestType, url, startTime, timeCode, resolve);
          // The xdr.onprogress event should always be defined, even as an empty function, or XDomainRequest may not fire onload for duplicate
          // requests see https://developer.mozilla.org/en-US/docs/Web/API/XDomainRequest
          request.onprogress = noop;
          // The xdr.send() call is wrapped in a timeout (see window.setTimeout() to prevent an issue with the interface where some requests are
          // lost if multiple XDomainRequests are being sent at the same time. see https://developer.mozilla.org/en-US/docs/Web/API/XDomainRequest
          setTimeout(() => {
            send(request, requestType, body);
          }, 0);
        } else {
          // To avoid checking the ready state of the request instance we will bind the response processing to the loadend event (readyState = 4)
          request.onloadend = () => processResponse(sensor, request, requestType, url, startTime, timeCode, resolve);
          send(request, requestType, body);
        }
      } else {
        logger.debug(`Request to target '${url}' cannot be sent due to lacking ajax support.`);
        state.runtimes += `${timeCode}:${new Date().getTime() - startTime}`;
        resolve();
      }
    });
  }
}
